<template>
  <section id="trackingRequestList" class="page-content">
    <header id="section-header">
      <p class="title has-text-grey">Shipments</p>
      <p class="subtitle has-text-grey">Search shipments.</p>
    </header>
    <main id="section-body">
      <div class="filters">
        <div class="filter">
          <label>Search</label>
          <div class="control">
            <input class="input" type="text" placeholder="Search for shipments..." v-model="filters.search"
              @keyup="debouncedGetAnswer" name="search" id="search">
          </div>
        </div>
        <div class="filter">
          <label>Shipment Status</label>
          <health-filter @change="filters.health = $event; debouncedGetAnswer()" />
        </div>
        <div class="filter">
          <label>Tracking Request Status</label>
          <div class="control">
            <button-group-filter @change="debouncedGetAnswer" :collection="STATUS_FILTER" :target="filters.status" />
          </div>
        </div>
        <div class="filter">
          <label class="ml-1">Management</label>
          <div class="control">
            <Button
              label="Configure Columns"
              class="button filter-reset is-primary is-small"
              icon="fa fa-lg fa-columns"
              iconPos="left"
              @click="showCustomColumns = true"
            />
            <Button
              label="Export List"
              class="button filter-reset is-primary is-small"
              icon="pi pi-file-excel"
              iconPos="left"
              @click="showExportForm = true"
            />
            <Button
              label="Filter"
              class="button filter-reset is-primary is-small"
              icon="pi pi-filter-fill"
              iconPos="left"
              @click="showCustomFilters = true"
            />
            <Button
              label="Reset Filters"
              class="button filter-reset is-primary is-small"
              icon="pi pi-eraser"
              iconPos="left"
              @click="clearFilters"
            />
          </div>
        </div>
      </div>
      <div v-if="tags.length > 0" class="filter-section">
        <Divider class="filter-list" align="center">
          <div class="inline-flex align-items-center">
            <i class="pi pi-filter-fill mr-2"></i>
            <b>Current filter list</b>
          </div>
        </Divider>
        <div class="grid">
          <div v-for="(tag, index) in tags" :key="index" class="tags-container col-12 md:col-6">
            <div class="tags-special">
              <Chip class="tag-special black">{{ tag.name }}</Chip>
              <Chip class="tag-special grey">{{ tag.type }}</Chip>
              <Chip class="tag-special green">{{ tag.value }}</Chip>
              <span class="close-icon" @click="removeFilter(tag, index)">&times;</span>
            </div>
          </div>
        </div>
      </div>
      <spinner v-show="loadingList"></spinner>
      <div v-show="!loadingList">
        <data-table url="shipment" :title="'Shipments'" :objects="objects" :schema="schema" :totalItems="count"
          :fields="columns" :ordering="orderingColumns" @order="filters.ordering = $event; applyFilters()" :alias="{
            'status_verbose': 'Milestone',
            'health': 'Shipment Status',
            'container_type_str': 'Container Type',
            'last_alert_code': 'Last Alert Event',
            'pod_timeofarrival_warning': 'POD Arrival Risk',
            'client_code': 'Client Code',
            'carrier_name': 'Carrier',
            'bl_or_booking_number': 'BL / Booking',
            'current_vessel': 'Current Vessel',
            'pol_loc': 'POL',
            'pod_loc': 'POD',
            'leg1_voyage': 'Leg 1 Voyage',
            'leg2_voyage': 'Leg 2 Voyage',
            'leg3_voyage': 'Leg 3 Voyage',
            'leg4_voyage': 'Leg 4 Voyage',
            'leg5_voyage': 'Leg 5 Voyage',
            'leg1_vessel': 'Leg 1 Vessel',
            'leg2_vessel': 'Leg 2 Vessel',
            'leg3_vessel': 'Leg 3 Vessel',
            'leg4_vessel': 'Leg 4 Vessel',
            'leg5_vessel': 'Leg 5 Vessel',
            'etd': 'ETD',
            'atd': 'ATD',
            'dtd': 'DTD',
            'eta': 'ETA',
            'ata': 'ATA',
            'dta': 'DTA',
            'discrepancy_eta_at_pod': 'DTAcP',
            'discrepancy_eta_at_dlv': 'DTAcD',
            'days_in_pol': 'DPOL',
            'days_in_tsp': 'DTSP',
            'days_in_pod': 'DPOD',
            'days_in_tsp_planned_initial': 'DTSP Planned Initial',
            'days_in_pod_planned_initial': 'DPOD Planned Initial',
            'days_in_pol_planned_initial': 'DPOL Planned Initial',
            'days_in_tsp_planned_last': 'DTSP Planned Last',
            'days_in_pod_planned_last': 'DPOD Planned Last',
            'days_in_pol_planned_last': 'DPOL Planned Last',
            'storage_days_at_pol': 'Storage days at POL',
            'storage_days_at_pod': 'Storage days at POD',
            'storage_days_at_pol_planned': 'Storage Days at POL Planned',
            'storage_days_at_pod_planned': 'Storage days at POD Planned',
            'days_between_empty_return_and_pod_departure': 'POD to Empty Return days',
            'days_between_empty_return_and_lif_departure': 'LIF to Empty Return days',
            'days_between_empty_return_and_pod_departure_planned': 'POD to Empty Return planned days',
            'days_between_empty_return_and_lif_departure_planned': 'LIF to Empty Return planned days',
            'created_at': 'Created',
            'sf_data': 'C. Form',
          }" :mappers="{ 'health': HEALTH }" :tooltips="{
            'last_alert_code': 'last_alert_message',
            'sf_data': 'sf_data',
            'pod_timeofarrival_warning': 'pod_timeofarrival_warning_tooltip'
          }" :modalFields="['last_alert_code']" @modal="openLastEvents($event)">
          <template slot="health" slot-scope="scope">
            <span class="status-tag" :class="scope.obj.value">{{ scope.obj.formated }}</span>
          </template>
          <div slot-scope="actions">
            <schema-form-modal :defaultForm="companyDefaultForm" :shipment="actions.object">
              <div v-if="notEmptyObject(companyDefaultForm) || notEmptyObject(actions.object.schema_form)" slot="trigger"
                class="button is-success is-small icon">
                <i class="fa fa-edit" style="font-size: 1.5em" />
              </div>
            </schema-form-modal>
          </div>
        </data-table>
        <pagination :currentPage.sync="page" :totalItems="count" :itemsPerPage="pageSize" />
      </div>
      <modal v-if="showLastEvents" @close="closeEvents()" width="95%">
        <div slot="header" style="display: flex">
          <h3 style="font-size:2em">Last Alert Events</h3>
          <div style="text-align: right; flex: 1">
            <button class="button is-success" @click="unhideAll()">Unhide All Events</button>
            <button class="button is-black" @click="hideAll()">Hide All Events</button>
          </div>
        </div>
        <div slot="body">
          <table class="table is-fullwidth is-striped is-bordered is-hoverable">
            <thead>
              <th id="data-table-header">Code</th>
              <th id="data-table-header">Message</th>
              <th id="data-table-header">WallTime</th>
              <th id="data-table-header">Hidden</th>
            </thead>
            <tbody>
              <tr class="object" v-for="event in lastEvents" :key="event.id">
                <td>{{ event.code_description }}</td>
                <td>{{ event.details.message }}</td>
                <td>{{ event.walltime }}</td>
                <td style="text-align:center">
                  <input type="checkbox" v-model="event.hidden" @change="saveEvent(event)" />
                  <span class="checkmark" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </modal>
    </main>
    <modal v-if="showExportForm" @close="showExportForm = false" width="60%">
      <div slot="header" style="font-size:18px;font-weight:600;margin-bottom:-15px;" class="title">Export List</div>
      <page-export slot="body" />
    </modal>
    <modal v-if="showCustomColumns" @close="showCustomColumns = false" width="90%">
      <div slot="header" style="font-size:18px;font-weight:600;margin-bottom:-15px;" class="title">Pick the columns you
        want to show
      </div>
      <custom-columns slot="body" :columns="tableHeaders" :previousSelected="selectedHeaders"
        @close="showCustomColumns = false; updateHeaders($event)" />
    </modal>
    <modal v-if="showCustomFilters" @close="showCustomFilters = false" width="45%">
      <div slot="header" style="font-size:18px;font-weight:600;margin-bottom:-15px;" class="title">Filters</div>
      <custom-filters slot="body" :customFilters="customFilters" :previousFilters="filters" :previousTags="tags"
        @apply="updateFilters($event)" />
    </modal>
  </section>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { filterElementsShipmentList, mapFilterTypes } from '@/models/filters/FilterTypes'
import SchemaFormModal from '@/components/SchemaFormModal'
import PaginatedFilteredListMixin from '@/pages/mixins/PaginatedFilteredListMixin'
import PageExport from '@/pages/PageExport'
import ButtonGroupFilter from '@/components/ButtonGroupFilter'
import DateRangeFilter from '@/components/DateRangeFilter'
import DataTable from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import Spinner from '@/components/Spinner'
import Modal from '@/components/Modal'
import HealthFilter from '@/components/HealthFilter'
import CustomColumns from '@/components/CustomColumns'
import CustomFilters from '@/components/CustomFilters'
import _ from 'lodash'

import Shipment from '@/models/shipment'

export default {
  name: 'shipmentList',
  props: ['userInfo'],
  components: { ButtonGroupFilter, DateRangeFilter, DataTable, Pagination, Spinner, Modal, PageExport, HealthFilter, SchemaFormModal, CustomColumns, CustomFilters },
  mixins: [PaginatedFilteredListMixin],
  created () {
    this.debouncedGetAnswer = _.debounce(this.applyFilters, 500)
  },
  mounted () {
    this.$emit('loading')
    this.loadHeaders()
    const promises = [
      this.getCarriers().then(response => {
        this.carriers = response
        this.getMilestones().then(response => {
          this.milestones = response
          this.customFilters = filterElementsShipmentList()
          this.filterTypes = mapFilterTypes()
          this.tags = this.updateTags()
        })
      }),
      this.getCodeTypes().then(response => { this.codeTypes = response }),
      this.companyDefaultForm = {},
      this.getUserInfo().then(response => {
        this.user = response
        this.companyDefaultForm = this.user.userprofile.company.attachment_schema_form
      }),
      this.getPage(this.page)
    ]
    Promise.all(promises).then(data => { this.$emit('loaded') })
    if (this.$route.query.health.length > 0) {
      this.filters.health = this.$route.query.health[0]
    }
  },
  data () {
    return {
      orderingColumns: [
        'id',
        'status_verbose',
        'health',
        'last_alert_code',
        'last_alert_walltime',
        'container_number',
        'bl_or_booking_number',
        'client_code',
        'carrier_name',
        'pol_loc',
        'pod_loc',
        'etd',
        'atd',
        'dtd',
        'eta',
        'ata',
        'dta',
        'discrepancy_eta_at_pod',
        'discrepancy_eta_at_dlv',
        'days_in_pol',
        'days_in_pod',
        'storage_days_at_pol',
        'storage_days_at_pod',
        'days_in_pol_planned',
        'days_in_pod_planned',
        'storage_days_at_pol_planned',
        'storage_days_at_pod_planned',
        'days_between_empty_return_and_pod_departure',
        'days_between_empty_return_and_lif_departure',
        'days_between_empty_return_and_pod_departure_planned',
        'days_between_empty_return_and_lif_departure_planned',
        'last_update',
        'created_at'
      ],
      fixedHeaders: ['id'],
      openFilters: false,
      showLastEvents: false,
      lastEvents: [],
      tableHeaders: [
        'status_verbose',
        'health',
        'last_alert_code',
        'last_alert_walltime',
        'pod_timeofarrival_warning',
        'container_number',
        'bl_or_booking_number',
        'client_code',
        'carrier_name',
        'current_vessel',
        'empty_pickup_loc',
        'origin_loc',
        'pol_loc',
        'leg1_voyage',
        'leg1_vessel',
        'tsp1_loc',
        'leg2_voyage',
        'leg2_vessel',
        'tsp2_loc',
        'leg3_voyage',
        'leg3_vessel',
        'tsp3_loc',
        'leg4_voyage',
        'leg4_vessel',
        'tsp4_loc',
        'leg5_voyage',
        'leg5_vessel',
        'pod_loc',
        'lif_loc',
        'dlv_loc',
        'empty_return_loc',
        'etd',
        'atd',
        'dtd',
        'eta',
        'ata',
        'dta',
        'discrepancy_eta_at_pod',
        'discrepancy_eta_at_dlv',
        'days_in_pol',
        'days_in_pol_planned',
        'days_in_pod',
        'days_in_pod_planned',
        'days_in_tsp',
        'days_in_tsp_planned',
        'storage_days_at_pol',
        'storage_days_at_pol_planned',
        'storage_days_at_pod',
        'storage_days_at_pod_planned',
        'days_between_empty_return_and_pod_departure',
        'days_between_empty_return_and_pod_departure_planned',
        'days_between_empty_return_and_lif_departure',
        'days_between_empty_return_and_lif_departure_planned',
        'sf_data',
        'last_update',
        'created_at'
      ],
      selectedHeaders: [],
      showCustomColumns: false,
      schema: Shipment.schema(),
      HEALTH: Shipment.HEALTH,
      STATUS_FILTER: Shipment.STATUS_FILTER,
      objects: [],
      codeTypes: [],
      carriers: [],
      milestones: [],
      companyDefaultForm: {},
      page: 1,
      showExportForm: false,
      showCustomFilters: false,
      filters: this.initializeFilters(),
      customFilters: [],
      filterTypes: [],
      tags: []
    }
  },
  methods: {
    ...mapActions({
      getCarriers: 'carrier/list',
      getMilestones: 'milestone/list',
      getCodeTypes: 'codeType/list',
      getUserInfo: 'user/readUserConfig',
      saveUserInfo: 'user/saveUserConfig',
      getObjects: 'shipment/list',
      getLastEvents: 'shipment/lastEvents',
      hideLastEvents: 'shipment/hideEvent',
      unhideLastEvents: 'shipment/unhideEvent',
      hideAllLastEvents: 'shipment/hideAllEvents',
      unhideAllLastEvents: 'shipment/unhideAllEvents'
    }),

    /**
     ** Checks if the provided object is not empty. Returns true if the object has properties, otherwise returns false.
     * @returns {void}
     */
    notEmptyObject (someObject) {
      if (someObject) {
        var parsedobj = JSON.parse(JSON.stringify(someObject))
        return Object.keys(parsedobj).length
      } else {
        return false
      }
    },

    /**
     ** Sets the selected shipment ID, retrieves the last events for the specified shipment, and displays the last events panel.
     * @param {Object} row - The row object representing the shipment.
     */
    openLastEvents (row) {
      this.shipment = row.id
      this.getLastEvents(row.id).then((response) => {
        this.lastEvents = response
      })
      this.showLastEvents = true
    },

    /**
     ** Saves the visibility status of an event by hiding or unhiding it.
     * @param {Object} event - The event object to be saved.
     */
    saveEvent (event) {
      if (event.hidden) {
        this.hideLastEvents(event.id).then((response) => {
        })
      } else {
        this.unhideLastEvents(event.id).then((response) => {
        })
      }
    },

    /**
     ** Hides all events associated with the current shipment.
     * @returns {void}
     */
    hideAll () {
      this.hideAllLastEvents(this.shipment).then((response) => {
        this.getLastEvents(this.shipment).then((response) => {
          this.lastEvents = response
        })
      })
    },

    /**
     ** Unhides all events associated with the current shipment.
     * @returns {void}
     */
    unhideAll () {
      this.unhideAllLastEvents(this.shipment).then((response) => {
        this.getLastEvents(this.shipment).then((response) => {
          this.lastEvents = response
        })
      })
    },

    /**
     ** Closes the event viewer modal and reloads the shipment tracking requests table.
     * @returns {void}
     */
    closeEvents () {
      this.loadingTable = true
      var promises = [
        this.getPage(this.page).then(
          response => {
            this.trackingRequests = response
          }
        )
      ]
      Promise.all(promises).then(() => {
        this.loadingTable = false
      })
      this.showLastEvents = false
    },

    /**
     ** Updates the selected headers for the shipments table and saves the changes to the user's profile.
     * @param {Array} headers - The selected headers to be updated.
     */
    updateHeaders (headers) {
      this.selectedHeaders = headers
      var user = this.userInfo.userprofile
      user.shipments_fields = this.selectedHeaders
      this.saveUserInfo({ id: this.userInfo.id, userprofile: user })
    },

    /**
     ** Loads the selected headers for the shipments table from the user's profile, if available.
     * @returns {void}
     */
    loadHeaders () {
      this.selectedHeaders = this.tableHeaders.slice()
      if (this.userInfo && this.userInfo.userprofile && this.userInfo.userprofile.shipments_fields) {
        this.selectedHeaders = this.userInfo.userprofile.shipments_fields
      }
    },

    /**
     ** Updates the filters based on the provided object. If the object has no filters, it resets the filters and clears the tags. Otherwise, it merges the filters with the existing ones, applies the filters, and updates the tags.
     * @param {Object} obj - The object containing filters and tags to update.
     */
    updateFilters (obj) {
      if (Object.keys(obj.filters).length === 0) {
        this.resetFilters()
        this.tags = []
      } else {
        this.filters = { ...this.filters, ...obj.filters }
        this.applyFilters()
        this.tags = obj.tags
      }
    },

    /**
     ** Updates the tags based on the current filters. Generates tags representing the applied filters and their values, and adds them to the tags array.
     * @returns {Array} - An array of objects representing the tags.
     */
    updateTags () {
      const tags = []

      for (const filterKey in this.filters) {
        let filterValue = this.filters[filterKey]
        const filterElement = this.customFilters.find(filter => filterKey.includes(filter.code))
        if (!filterElement) continue

        const key = filterElement.code

        for (const filterType of filterElement.types) {
          const filterTypeData = this.filterTypes[filterType]
          if (!filterTypeData) continue

          const params = filterTypeData.params
          const codeParams = params.length > 0 ? params.map(param => key + '_' + param) : [key]

          if (params.length > 0) {
            for (const param of filterTypeData.params) {
              if (!filterKey.includes(param)) continue

              if (filterValue) {
                const existingTag = tags.find(tag => tag.name === filterElement.name && tag.type === filterTypeData.name)
                if (existingTag) existingTag.value += ` To ${filterValue}`
                else {
                  if (param === 'isnull') filterValue = filterValue ? 'No' : 'Yes'
                  tags.push({
                    name: filterElement.name,
                    type: filterTypeData.name,
                    code: codeParams,
                    value: filterValue
                  })
                }
              }
            }
          } else {
            const paramName = `${filterKey}`
            let value = filterValue

            if ((Array.isArray(value) && value.length > 0) || (!Array.isArray(value) && value)) {
              if (paramName === 'carrier' && Array.isArray(value) && value.length > 0) {
                value.forEach(id => {
                  const carrier = this.carriers.find(carrier => carrier.id === parseInt(id))
                  tags.push({
                    name: filterElement.name,
                    type: filterTypeData.name,
                    code: codeParams,
                    value: carrier.official_name,
                    id: carrier.id
                  })
                })
              } else if (paramName === 'shipment_status' && Array.isArray(value) && value.length > 0) {
                value.forEach(id => {
                  const milestone = this.milestones.find(milestone => milestone.id === parseInt(id))
                  tags.push({
                    name: filterElement.name,
                    type: filterTypeData.name,
                    code: codeParams,
                    value: milestone.description,
                    id: milestone.id
                  })
                })
              } else {
                tags.push({
                  name: filterElement.name,
                  type: filterTypeData.name,
                  code: codeParams,
                  value: value
                })
              }
            }
          }
        }
      }

      return tags
    },

    /**
     ** Removes a filter based on the provided tag and its index in the tags array. Depending on the tag's code, it removes the corresponding filter from the filters object. Additionally, it removes the tag from the tags array and applies the updated filters.
     * @param {Object} tag - The tag object to be removed.
     * @param {number} index - The index of the tag in the tags array.
     */
    removeFilter (tag, index) {
      if (tag.code[0] === 'carrier') this.filters.carrier = this.filters.carrier.filter(id => id.toString() !== tag.id.toString())
      else if (tag.code[0] === 'shipment_status') this.filters.shipment_status = this.filters.shipment_status.filter(id => id.toString() !== tag.id.toString())
      else this.filters[tag.code[0]] = null
      if (tag.code[1]) this.filters[tag.code[1]] = null
      this.tags.splice(index, 1)
      this.applyFilters()
    },

    /**
     ** Clears all filters by resetting them to their default values and clears the tags array.
     * @returns {void}
     */
    clearFilters () {
      this.resetFilters()
      this.tags = []
    },

    /**
     ** Initializes a new filters object with default values based on predefined filter elements and types. Returns an object containing default values for each filter element and type.
     * @returns {void}
     */
    initializeFilters () {
      const filterTemplate = {}
      const filterTypes = mapFilterTypes()
      const filterElements = filterElementsShipmentList()

      filterElements.forEach(element => {
        const code = element.code
        const types = element.types

        types.forEach(type => {
          const filterType = filterTypes[type]
          const params = filterType.params.reduce((accumulator, param) => {
            accumulator[`${code}_${param}`] = null
            return accumulator
          }, {})

          Object.assign(filterTemplate, params)
        })
      })

      filterTemplate.status = []
      filterTemplate.health = []
      filterTemplate.order_dta = ''
      filterTemplate.search = ''
      filterTemplate.pol_loc = ''
      filterTemplate.pod_loc = ''
      filterTemplate.dta = ''
      filterTemplate.carrier = []
      filterTemplate.shipment_status = []

      return filterTemplate
    }
  },
  computed: {
    ...mapState({
      count: state => state.shipment.count,
      pageSize: state => state.pageSize
    }),
    ...mapGetters({ hasPermission: 'hasPermission' }),
    columns () {
      var selectedHeaders = this.selectedHeaders.filter((x) => this.tableHeaders.indexOf(x) >= 0)
      let columns = this.fixedHeaders.concat(selectedHeaders)
      if (selectedHeaders.length === 0) {
        columns = columns.concat(this.tableHeaders)
      }
      return [...new Set(columns)]
    }
  },
  watch: {
    'page' () { this.getPage(this.page) },
    userInfo () {
      this.loadHeaders()
    }
  }
}
</script>
<style lang="scss">
.modal-container {
  overflow: auto;
  max-height: 95vh;
}

.collapsable-filters {
  transition: all .3s ease-in-out;
  max-height: 0px;
  opacity: 0;

  &.open {
    max-height: 500px;
    opacity: 1;
  }

  .filter {
    max-width: 320px;
  }
}

@media (max-width: 620px) {
  .collapsable-filters.open {
    max-height: 1500px;
    opacity: 1;
  }
}

.ml-1 {
  margin-left: 1%;
}

.tags-container {
  padding: 10px !important;
  border: 1px solid #ccc;
  border-radius: 6px 6px 6px 6px;
  margin: 0px 5px 5px 5px;
}

@media screen and (min-width: 768px) {
  .md\:col-6 {
    width: 49% !important;
  }
}

.tags-special {
  align-items: center;
  gap: 3px;
}

.tag-special {
  border-radius: 5px !important;
  color: #fff !important;

  &.black {
    background-color: black;
  }

  &.grey {
    background-color: grey;
  }

  &.green {
    background-color: #009c95;
  }
}

.close-icon {
  float: right;
  margin-right: 5px;
  font-weight: bold;
  background-color: grey;
  padding: 1px 6px;
  border-radius: 5px;
  color: white;
  cursor: pointer;
}

.filter-list .p-divider-content {
  background-color: #F5F7FA !important;
}

.p-divider-solid.p-divider-horizontal:before {
  border-top-style: solid !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/PageShipmentList.vue